<template>
  <div class="productCard" data-qa="product-card-container">
    <div
      class="card"
      v-for="app in filteredApps"
      :key="app.label"
      :data-qa="`product-card-${app.label}`"
      role="button"
      tabindex="0"
      @click.stop="openApp(app.label, app.link)"
      @keydown.enter.stop="openApp(app.label, app.link)"
      @keydown.space.prevent.stop="openApp(app.label, app.link)"
      :aria-label="app.displayLabel ?? app.label"
    >
      <div class="card-content" :data-qa="`card-content-${app.label}`">
        <div class="card-header" :data-qa="`card-header-${app.label}`">
          <cs-pictogram
            height="2.5rem"
            width="2.5rem"
            :name="app.pictoName"
            :alt="app.label + ' logo'"
            :data-qa="`app-logo-${app.label}`"
          />
          <div
            class="card-actions"
            :class="{ 'card-actions--pinned': isFavorite(app.label) }"
          >
            <button
              type="button"
              class="pin-btn"
              :data-qa="`pin-button-${app.label}`"
              :aria-label="
                isFavorite(app.label) ? t('dashboard.unpin') : t('dashboard.pin')
              "
              :aria-pressed="isFavorite(app.label)"
              @click.stop="togglePin(app.label)"
            >
              <cs-icon
                height="1rem"
                width="1rem"
                class="pin"
                :name="isFavorite(app.label) ? 'epingle' : 'epinglable'"
                role="presentation"
                tabindex="-1"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div class="title-icons">
          <p class="title" :data-qa="`app-name-${app.label}`">
            {{ app.displayLabel ?? app.label }}
          </p>
          <div class="icons" :data-qa="`card-icons-${app.label}`">
            <el-icon
              v-if="phoneEnabledApps.includes(app.label)"
              class="icon"
              :data-qa="`phone-icon-${app.label}`"
              aria-hidden="true"
              role="img"
              :aria-label="t('productIcon.phoneIcon')"
            >
              <iphone />
            </el-icon>
            <el-icon
              class="icon"
              :data-qa="`monitor-icon-${app.label}`"
              aria-hidden="true"
              role="img"
              :aria-label="t('productIcon.monitorIcon')"
            >
              <Monitor />
            </el-icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Iphone, Monitor } from '@element-plus/icons-vue';
import { computed } from 'vue';
import { redirect } from '@/utils/ui';
import { useI18n } from 'vue-i18n';
import { updateRecentApps } from '@/utils/recentApps';
import { UseStoreUser } from '@/store/userStore';
import { UserInDb } from '@/types/UserInDb';
import type { UserApp } from '@/types/user';

const phoneEnabledApps = ['teamsportailrh'];
const userStore = UseStoreUser();

const { t } = useI18n();

const props = defineProps<{
  apps: UserApp[];
  searchKeyword: string;
  favoriteList: string[];
}>();
const handleCommand = async (
  appLabel: string,
  command: string | number | object
) => {
  if (command == 'pin') {
    const userUpdated = Object.assign({}, userStore.userInDb);
    userUpdated.favorite = props.favoriteList
      ? [appLabel, ...props.favoriteList]
      : [appLabel];
    await updateUserFavorite(userUpdated);
  } else if (command == 'unpin') {
    const userUpdated = Object.assign({}, userStore.userInDb);
    userUpdated.favorite = props.favoriteList
      ? props.favoriteList.filter(favorite => favorite !== appLabel)
      : [];
    await updateUserFavorite(userUpdated);
  }
};
async function updateUserFavorite(userData: UserInDb) {
  try {
    await userStore.updateFavorite(userData);
    console.log('favorite updated successfully!');
  } catch (err) {
    console.log(err);
  }
}
const filteredApps = computed(() => {
  const list = props.apps || [];
  const filtered = list.filter(app =>
    app.label.toUpperCase().includes(props.searchKeyword.toUpperCase())
  );
  const favoriteListInApps = props.favoriteList
    ? filtered.filter(app => props.favoriteList!.includes(app.label))
    : [];
  const appsNotFavorite = filtered.filter(
    app => !props.favoriteList?.includes(app.label)
  );
  return [...favoriteListInApps, ...appsNotFavorite];
});

function isFavorite(item: string) {
  if (props.favoriteList) {
    return props.favoriteList.includes(item);
  }
  return false;
}

function togglePin(appLabel: string) {
  const command = isFavorite(appLabel) ? 'unpin' : 'pin';
  handleCommand(appLabel, command);
}

const emit = defineEmits<{
  (e: 'opened'): void;
  (e: 'clicked', appId: string): void;
}>();

function openApp(appId: string, url: string) {
  // Emit click event for Matomo tracking
  emit('clicked', appId);
  updateRecentApps(appId);
  emit('opened');
  setTimeout(() => redirect(url), 50);
}
</script>

<style scoped>
.card {
  cursor: pointer;
  width: 14.5rem;
  height: 7.2rem;
  background-color: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 0.5rem;
  display: flex;
  padding: 0.25rem 0.75rem;
  gap: 0.5rem;
  min-width: 14rem;
  margin-bottom: 1.5rem;
  margin-top: 0.5rem;
}

.card img.logo {
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
}

.productCard {
  display: flex;
  gap: 1rem;
}

.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}
.title-icons {
  display: flex;
}
.card-header {
  display: flex;
  justify-content: space-between;
}
.title {
  margin-right: 0.4rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.125rem;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-family: Nunito, sans-serif;
}

.card:hover {
  border: 1px solid rgba(1, 103, 255, 1);
  background: rgba(230, 240, 255, 1);
}

.card-actions {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.card:hover .card-actions {
  opacity: 1;
  pointer-events: auto;
}

.card-actions--pinned {
  opacity: 1;
  pointer-events: auto;
}

.pin-btn {
  height: 1.5rem;
  width: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
}

.pin-btn:hover {
  border-radius: 0.2rem;
  background-color: #a7b1bf36;
}

.icons {
  font-size: 0.75rem;
  color: #999;
  margin-left: 0.4rem;
}

.icon {
  margin-right: 0.2rem;
  margin-top: 0.2rem;
}

p {
  margin: 0;
}

.pin {
  margin-left: 0.1rem;
}
</style>
